import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './EvaluationResults.css';

const EvaluationResults = () => {
  const [searchParams] = useSearchParams();
  const { runId } = useParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    agent_name: searchParams.get('agent_name') || '',
    passed: searchParams.get('passed') || '',
    run_id: runId || searchParams.get('run_id') || ''
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchResults();
  }, [filters, pagination.offset]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });

      if (filters.agent_name) params.append('agent_name', filters.agent_name);
      if (filters.passed) params.append('passed', filters.passed);
      if (filters.run_id) params.append('run_id', filters.run_id);

      const response = await apiFetch(`/api/evals/results?${params}`);
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setResults(data.results || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));
      } else {
        setError(
          getUserFacingErrorMessage(response.status, data, 'Failed to load evaluation results')
        );
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(
        err.message && err.name === 'TypeError' && err.message.includes('fetch')
          ? 'Could not reach the server. Check your connection and try again.'
          : err.message || 'Failed to load evaluation results'
      );
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleRowExpansion = (resultId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Run ID', 'Agent', 'Sample ID', 'Passed', 'Created At'];
    const rows = results.map(r => [
      r.id,
      r.run_id,
      r.agent_name,
      r.sample_id || 'N/A',
      r.passed ? 'Yes' : 'No',
      r.created_at
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_results_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(results, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_results_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const sortedResults = [...results].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal === bVal) return 0;

    const comparison = aVal > bVal ? 1 : -1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return (
      <div className="evaluation-results">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evaluation-results">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchResults} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="evaluation-results">
      <div className="results-header">
        <h1>Evaluation Results</h1>
        <div className="header-actions">
          <button onClick={exportToCSV} className="btn-secondary">
            Export CSV
          </button>
          <button onClick={exportToJSON} className="btn-secondary">
            Export JSON
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Agent:</label>
          <select
            value={filters.agent_name}
            onChange={(e) => handleFilterChange('agent_name', e.target.value)}
          >
            <option value="">All Agents</option>
            <option value="extractor">Extractor</option>
            <option value="evaluator">Evaluator</option>
            <option value="summarizer">Summarizer</option>
            <option value="formatter">Formatter</option>
            <option value="explainability">Explainability</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.passed}
            onChange={(e) => handleFilterChange('passed', e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Passed</option>
            <option value="false">Failed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Run ID:</label>
          <input
            type="number"
            value={filters.run_id}
            onChange={(e) => handleFilterChange('run_id', e.target.value)}
            placeholder="Filter by run ID"
          />
        </div>
      </div>

      <div className="results-info">
        <p>
          Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
        </p>
      </div>

      {results.length === 0 ? (
        <div className="empty-state">
          <p>No evaluation results found matching the filters.</p>
        </div>
      ) : (
        <div className="results-table-container">
          <table className="results-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('agent_name')} className="sortable">
                  Agent {sortConfig.key === 'agent_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('sample_id')} className="sortable">
                  Sample ID {sortConfig.key === 'sample_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('passed')} className="sortable">
                  Passed {sortConfig.key === 'passed' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('created_at')} className="sortable">
                  Created At {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((result) => {
                const isExpanded = expandedRows.has(result.id);
                return (
                  <React.Fragment key={result.id}>
                    <tr>
                      <td>{result.id}</td>
                      <td>{result.agent_name}</td>
                      <td>{result.sample_id || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${result.passed ? 'status-passed' : 'status-failed'}`}>
                          {result.passed ? 'Passed' : 'Failed'}
                        </span>
                      </td>
                      <td>
                        {result.created_at
                          ? new Date(result.created_at).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td>
                        <button
                          onClick={() => toggleRowExpansion(result.id)}
                          className="btn-link"
                        >
                          {isExpanded ? 'Hide' : 'Show'} Details
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="expanded-row">
                        <td colSpan="6">
                          <div className="result-details">
                            <div className="detail-section">
                              <h4>Input Data</h4>
                              <pre>{JSON.stringify(result.input_data, null, 2)}</pre>
                            </div>
                            <div className="detail-section">
                              <h4>Expected Output</h4>
                              <pre>{JSON.stringify(result.expected_output, null, 2)}</pre>
                            </div>
                            <div className="detail-section">
                              <h4>Actual Output</h4>
                              <pre>{JSON.stringify(result.actual_output, null, 2)}</pre>
                            </div>
                            {result.metrics && (
                              <div className="detail-section">
                                <h4>Metrics</h4>
                                <pre>{JSON.stringify(result.metrics, null, 2)}</pre>
                              </div>
                            )}
                            {result.error_message && (
                              <div className="detail-section error">
                                <h4>Error</h4>
                                <p>{result.error_message}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination">
        <button
          onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
          disabled={pagination.offset === 0}
          className="btn-secondary"
        >
          Previous
        </button>
        <span>
          Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
        </span>
        <button
          onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
          disabled={pagination.offset + pagination.limit >= pagination.total}
          className="btn-secondary"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default EvaluationResults;



